/**
 * What this build can do (`03-client-app.md` section 1.1).
 *
 * Screens never check `platform` directly. They check a capability, so adding a platform means
 * filling in a row here rather than hunting for `if (platform === ...)` across the app.
 */

export type Platform = 'web' | 'windows' | 'linux' | 'android';

export interface Capabilities {
  installWindowsGames: boolean;
  installAndroidGames: boolean;
  uploadBuilds: boolean;
  /**
   * True where the script editor can open a folder on this machine.
   *
   * It needs a real filesystem and a keyboard, and it saves everything to disk and nothing to
   * the cloud, so the two builds that have neither are left out: a browser tab cannot be given
   * a folder without asking for a permission we would rather not hold, and a phone has the
   * folder but not the keyboard. Both are shown the way to a desktop instead.
   */
  editScripts: boolean;
  /**
   * True when this build can send the user straight to a hosted checkout page. The apps hand off
   * to the web app instead, because payment always happens on goodbuild.cc.
   */
  hostedCheckout: boolean;
  /**
   * True where a folder can be handed to the desktop's own file manager.
   *
   * Windows and Linux have one and it is a press away from the library. A browser tab cannot
   * open a folder on the machine it is running on, and on Android a game's files sit where no
   * file manager will show them, so on both the item is simply not offered.
   */
  revealFiles: boolean;
  osNotifications: boolean;
  secureTokenStorage: boolean;
  /**
   * True where Big Picture is a mode there is anything to switch into: a window that can take
   * the whole screen, or a browser tab that can ask to. Android is already the whole screen and
   * is driven by a thumb rather than a stick, so it has nothing to offer.
   */
  bigPicture: boolean;
}

/** True when the page is running inside a Tauri webview. */
export function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

function detectPlatform(): Platform {
  if (!isTauri()) return 'web';
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/windows|win32|win64/i.test(ua)) return 'windows';
  return 'linux';
}

export const platform: Platform = detectPlatform();

const CAPABILITIES: Record<Platform, Capabilities> = {
  web: {
    installWindowsGames: false,
    installAndroidGames: false,
    uploadBuilds: false,
    editScripts: false,
    hostedCheckout: true,
    revealFiles: false,
    osNotifications: false,
    secureTokenStorage: false,
    bigPicture: true,
  },
  windows: {
    installWindowsGames: true,
    installAndroidGames: false,
    uploadBuilds: true,
    editScripts: true,
    hostedCheckout: false,
    revealFiles: true,
    osNotifications: true,
    secureTokenStorage: true,
    bigPicture: true,
  },
  linux: {
    // Steam Deck runs Windows builds through Proton, so it installs the same builds.
    installWindowsGames: true,
    installAndroidGames: false,
    uploadBuilds: true,
    editScripts: true,
    hostedCheckout: false,
    revealFiles: true,
    osNotifications: true,
    secureTokenStorage: true,
    bigPicture: true,
  },
  android: {
    installWindowsGames: false,
    installAndroidGames: true,
    uploadBuilds: false,
    editScripts: false,
    hostedCheckout: false,
    revealFiles: false,
    osNotifications: true,
    secureTokenStorage: true,
    bigPicture: false,
  },
};

export const capabilities: Capabilities = CAPABILITIES[platform];

/** The build platform this device can install, or null on the web. */
export const buildPlatform: 'windows' | 'android' | null = capabilities.installWindowsGames
  ? 'windows'
  : capabilities.installAndroidGames
    ? 'android'
    : null;

export const isDesktop = platform === 'windows' || platform === 'linux';
export const isNative = platform !== 'web';
